import * as React from 'react'
// antd's OWN field contexts, not transcribed copies — the same choice
// `../Input.shadcn` and `../Select/Select.shadcn` make, and for the same
// reason: they are what a real `<ConfigProvider componentDisabled>` /
// `<Form disabled>` / `<Form.Item>` on the page publishes, so the shadcn half
// stays inside antd's own cascade rather than beside it. `DisabledContext`
// carries the inherited disabled flag; `FormItemInputContext` carries the
// validation status and the "am I inside a Form.Item" flag. The `antd/es/*`
// subpath is load-bearing (AGENTS.md, the ES/CJS split-context trap): this
// repo's bundler and vitest both resolve bare `'antd'` to `es/`, so an
// `antd/lib/*` import would read a DIFFERENT context instance and always see
// the default.
import DisabledContext from 'antd/es/config-provider/DisabledContext'
import { FormItemInputContext } from 'antd/es/form/context'
import dayjs, { Dayjs } from 'dayjs'

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from 'src/components/ui/popover'
import {
  CalendarOutlined,
  ClockCircleOutlined,
  CloseCircleFilled,
} from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import {
  FormatProp,
  formatValue,
  isSameCell,
  parseValue,
  PickerMode,
  resolveFormats,
  ShowTimeConfig,
  snapToPicker,
  timeFormatFor,
} from './format'
import { usePickerLocale, useDayjsLocaleName, withLocale } from './locale'
import {
  CellRender,
  DateBody,
  decadeStart,
  DisabledTimeResult,
  HeaderViewButton,
  initialPanelMode,
  MonthBody,
  PanelHeader,
  PanelMode,
  QuarterBody,
  TimeBody,
  YearBody,
} from './panel'
// rc-util's `useMergedState`, reduced — shared with the Select seam rather
// than copied a second time. Its reset-on-`undefined` is what makes "clear it,
// then re-pick the SAME day" fire `onChange` again.
import { useMergedState } from '../Select/useMergedState'

/* -------------------------------------------------------------------------
 * The shadcn half of the `src/antd/DatePicker` seam: antd's `DatePicker` and
 * `DatePicker.RangePicker` rebuilt on `react-day-picker` (through the shadcn
 * `Calendar` primitive) + Radix `Popover`, behind antd's own props.
 *
 * Ported from `origin/update-design`, where antd is deleted. Three of that
 * branch's assumptions are false here and are corrected above and below:
 * the field contexts come from antd itself rather than from transcribed
 * copies; the public prop TYPES stay antd's (see `./index.tsx`); and the panel
 * re-emits no `.ant-picker-*` class name, because antd's own picker stylesheet
 * is still on the page (see the DOM-contract note in `./panel.tsx`).
 *
 * READ `./index.tsx` FIRST — it is the dispatcher, and it holds the statics.
 *
 * ---------------------------------------------------------------------------
 * WHAT DID NOT MOVE
 *
 * `dayjs` is untouched. Every format, parse, month name, weekday name, week
 * number and first-day-of-week comes from dayjs (see `./format`, `./locale`),
 * so `src/App/DayjsI18n` still decides how a Danish or Finnish user reads a
 * date, exactly as it did with antd's panel. react-day-picker lays out the day
 * grid and nothing else; its own date-fns locale data is never read.
 *
 * antd's `TimePicker` is a SEPARATE component and is deliberately NOT ported
 * (5 call sites; filed as a follow-up). `DatePicker.TimePicker` — antd's
 * `picker="time"` preset hung off DatePicker — IS ported, because it is this
 * component, and dropping a static is the recurring seam bug.
 *
 * ---------------------------------------------------------------------------
 * PARITY NOTES
 *
 * - `onChange(date, dateString)` keeps antd's payload exactly: a `Dayjs | null`
 *   plus the string formatted with the FIRST entry of `format`; the range form
 *   emits `[Dayjs|null, Dayjs|null]` and `[string, string]`. ~113 of this
 *   repo's 160 picker call sites are plumbed by `Form.Item` and never see the
 *   second argument; the ones that DO read it read it instead of the first
 *   (`WhDatePicker`, `DatePickerSimpleString`, `StepDefinition`,
 *   `EffectiveControl`, `StepWho`), which is why it is not "nice to have".
 * - `onChange` only fires on a real change (`isSameCell`), matching rc-picker.
 * - A range fires `onChange` only once the range is COMPLETE, and
 *   `onCalendarChange` on every partial pick — several call sites read
 *   `v[0]`/`v[1]` unguarded inside `onChange`.
 * - Controlled `value` goes through the same `useMergedState` copy the Select
 *   seam uses, whose reset-on-`undefined` is what makes "clear it, then re-pick
 *   the SAME day" fire `onChange` again.
 * - `showTime` commits on OK (antd's rule), a plain date commits on click.
 * ------------------------------------------------------------------------- */

export type { PickerMode }

type PickerRefLike = {
  nativeElement: HTMLDivElement | null
  focus: (options?: FocusOptions) => void
  blur: () => void
}

type AllowClear = boolean | { clearIcon?: React.ReactNode }

type SharedProps = {
  id?: string
  name?: string
  className?: string
  rootClassName?: string
  style?: React.CSSProperties
  popupClassName?: string
  dropdownClassName?: string
  popupStyle?: React.CSSProperties
  picker?: PickerMode
  format?: FormatProp
  showTime?: boolean | ShowTimeConfig
  showNow?: boolean
  showToday?: boolean
  disabled?: boolean
  inputReadOnly?: boolean
  allowClear?: AllowClear
  autoFocus?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  placement?: 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight'
  size?: 'small' | 'middle' | 'large'
  status?: '' | 'error' | 'warning'
  variant?: 'outlined' | 'borderless' | 'filled'
  bordered?: boolean
  suffixIcon?: React.ReactNode
  locale?: $TSFixMe
  minDate?: Dayjs
  maxDate?: Dayjs
  defaultPickerValue?: Dayjs
  disabledDate?: (date: Dayjs, info?: $TSFixMe) => boolean
  disabledTime?: (date: $TSFixMe, type?: $TSFixMe) => DisabledTimeResult
  /**
   * antd types the first argument `Dayjs | string | number` (the panel it
   * shipped reused one renderer for the time columns too). This panel only
   * ever hands back a `Dayjs`; the type stays antd's so call sites that
   * already type against it keep compiling.
   */
  cellRender?: (current: $TSFixMe, info: $TSFixMe) => React.ReactNode
  /** @deprecated antd 5 alias for `cellRender`. */
  dateRender?: (current: Dayjs, today: Dayjs) => React.ReactNode
  panelRender?: (panel: React.ReactNode) => React.ReactNode
  renderExtraFooter?: (mode: PanelMode) => React.ReactNode
  getPopupContainer?: (trigger: HTMLElement) => HTMLElement | null
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
  onOk?: $TSFixMe
  showWeekNumber?: boolean
}

/** antd's default suffix: a clock for a time picker, a calendar otherwise. */
const defaultSuffixIcon = (picker: PickerMode) =>
  picker === 'time' ? <ClockCircleOutlined /> : <CalendarOutlined />

const SIZE_CLASS: Record<string, string> = {
  small: 'h-6 px-2 text-xs',
  middle: 'h-9 px-3 text-sm',
  large: 'h-10 px-3 text-base',
}

const variantClass = (
  variant: SharedProps['variant'],
  bordered: SharedProps['bordered']
) => {
  const resolved = variant ?? (bordered === false ? 'borderless' : 'outlined')
  if (resolved === 'borderless')
    return 'border-transparent bg-transparent shadow-none'
  if (resolved === 'filled') return 'border-transparent bg-muted shadow-none'
  return undefined
}

const toShowTimeConfig = (
  showTime: boolean | ShowTimeConfig | undefined,
  picker: PickerMode
): ShowTimeConfig | null => {
  if (picker === 'time') return typeof showTime === 'object' ? showTime : {}
  if (!showTime) return null
  return typeof showTime === 'object' ? showTime : {}
}

/**
 * `minDate`/`maxDate` fold into the same predicate `disabledDate` uses so the
 * panel, the typed-text commit and the keyboard path all agree on one answer.
 */
const useDisabledPredicate = (
  disabledDate: SharedProps['disabledDate'],
  minDate?: Dayjs,
  maxDate?: Dayjs
) =>
  React.useCallback(
    (date: Dayjs, type: PanelMode = 'date') => {
      if (minDate && date.isBefore(minDate, 'day')) return true
      if (maxDate && date.isAfter(maxDate, 'day')) return true
      return Boolean(disabledDate?.(date, { type }))
    },
    [disabledDate, minDate, maxDate]
  )

const placeholderFor = (
  locale: $TSFixMe,
  picker: PickerMode,
  isRange: boolean
): string | [string, string] => {
  const lang = locale?.lang ?? {}
  if (isRange) {
    switch (picker) {
      case 'year':
        return lang.rangeYearPlaceholder ?? ['Start year', 'End year']
      case 'quarter':
        return lang.rangeQuarterPlaceholder ?? ['Start quarter', 'End quarter']
      case 'month':
        return lang.rangeMonthPlaceholder ?? ['Start month', 'End month']
      case 'week':
        return lang.rangeWeekPlaceholder ?? ['Start week', 'End week']
      default:
        return lang.rangePlaceholder ?? ['Start date', 'End date']
    }
  }
  switch (picker) {
    case 'year':
      return lang.yearPlaceholder ?? 'Select year'
    case 'quarter':
      return lang.quarterPlaceholder ?? 'Select quarter'
    case 'month':
      return lang.monthPlaceholder ?? 'Select month'
    case 'week':
      return lang.weekPlaceholder ?? 'Select week'
    case 'time':
      return locale?.timePickerLocale?.placeholder ?? 'Select time'
    default:
      return lang.placeholder ?? 'Select date'
  }
}

/* --------------------------------------------------------------- the panel */

type PanelProps = {
  picker: PickerMode
  panelMode: PanelMode
  setPanelMode: (mode: PanelMode) => void
  viewDate: Dayjs
  setViewDate: (date: Dayjs) => void
  localeName?: string
  locale: $TSFixMe
  isDisabledDate: (date: Dayjs, type?: PanelMode) => boolean
  cellRender?: CellRender
  selected?: Dayjs | null
  rangeSelection?: { from?: Dayjs | null; to?: Dayjs | null }
  onPick: (date: Dayjs) => void
  showWeekNumber?: boolean
  showPrev?: boolean
  showNext?: boolean
  rangeSide?: 'start' | 'end'
}

const PickerPanel = ({
  picker,
  panelMode,
  setPanelMode,
  viewDate,
  setViewDate,
  localeName,
  locale,
  isDisabledDate,
  cellRender,
  selected,
  rangeSelection,
  onPick,
  showWeekNumber,
  showPrev = true,
  showNext = true,
  rangeSide,
}: PanelProps) => {
  const lang = locale?.lang ?? {}
  const labels = {
    superPrev: lang.previousYear ?? 'Previous year',
    prev: lang.previousMonth ?? 'Previous month',
    next: lang.nextMonth ?? 'Next month',
    superNext: lang.nextYear ?? 'Next year',
  }

  const shared = {
    viewDate,
    localeName,
    cellRender,
    rangeSide,
  }

  if (panelMode === 'month') {
    return (
      <div data-slot="picker-panel" data-mode="month">
        <PanelHeader
          showPrev={showPrev}
          showNext={showNext}
          onPrev={() => setViewDate(viewDate.subtract(1, 'year'))}
          onNext={() => setViewDate(viewDate.add(1, 'year'))}
          labels={{ ...labels, prev: labels.superPrev, next: labels.superNext }}
        >
          <HeaderViewButton
            slot="picker-year-btn"
            onClick={() => setPanelMode('year')}
          >
            {withLocale(viewDate, localeName).format('YYYY')}
          </HeaderViewButton>
        </PanelHeader>
        <MonthBody
          {...shared}
          disabledDate={(date) => isDisabledDate(date, 'month')}
          selected={selected}
          onPick={(date) => {
            if (picker === 'month' || picker === 'quarter') onPick(date)
            else {
              setViewDate(date)
              setPanelMode(initialPanelMode(picker))
            }
          }}
        />
      </div>
    )
  }

  if (panelMode === 'quarter') {
    return (
      <div data-slot="picker-panel" data-mode="quarter">
        <PanelHeader
          showPrev={showPrev}
          showNext={showNext}
          onPrev={() => setViewDate(viewDate.subtract(1, 'year'))}
          onNext={() => setViewDate(viewDate.add(1, 'year'))}
          labels={{ ...labels, prev: labels.superPrev, next: labels.superNext }}
        >
          <HeaderViewButton
            slot="picker-year-btn"
            onClick={() => setPanelMode('year')}
          >
            {withLocale(viewDate, localeName).format('YYYY')}
          </HeaderViewButton>
        </PanelHeader>
        <QuarterBody
          {...shared}
          disabledDate={(date) => isDisabledDate(date, 'quarter')}
          selected={selected}
          onPick={onPick}
        />
      </div>
    )
  }

  if (panelMode === 'year') {
    const start = decadeStart(viewDate)
    return (
      <div data-slot="picker-panel" data-mode="year">
        <PanelHeader
          showPrev={showPrev}
          showNext={showNext}
          onPrev={() => setViewDate(viewDate.subtract(10, 'year'))}
          onNext={() => setViewDate(viewDate.add(10, 'year'))}
          labels={{
            ...labels,
            prev: lang.previousDecade ?? 'Last decade',
            next: lang.nextDecade ?? 'Next decade',
          }}
        >
          <HeaderViewButton slot="picker-decade-btn">
            {`${start}-${start + 9}`}
          </HeaderViewButton>
        </PanelHeader>
        <YearBody
          {...shared}
          disabledDate={(date) => isDisabledDate(date, 'year')}
          selected={selected}
          onPick={(date) => {
            if (picker === 'year') onPick(date)
            else {
              setViewDate(date)
              setPanelMode(picker === 'quarter' ? 'quarter' : 'month')
            }
          }}
        />
      </div>
    )
  }

  // date / week
  const monthLabel = withLocale(viewDate, localeName).format('MMMM')
  const yearLabel = withLocale(viewDate, localeName).format('YYYY')
  const monthBtn = (
    <HeaderViewButton
      key="month"
      slot="picker-month-btn"
      onClick={() => setPanelMode('month')}
    >
      {monthLabel}
    </HeaderViewButton>
  )
  const yearBtn = (
    <HeaderViewButton
      key="year"
      slot="picker-year-btn"
      onClick={() => setPanelMode('year')}
    >
      {yearLabel}
    </HeaderViewButton>
  )

  return (
    <div
      data-slot="picker-panel"
      data-mode={picker === 'week' ? 'week' : 'date'}
    >
      <PanelHeader
        showPrev={showPrev}
        showNext={showNext}
        onSuperPrev={() => setViewDate(viewDate.subtract(1, 'year'))}
        onPrev={() => setViewDate(viewDate.subtract(1, 'month'))}
        onNext={() => setViewDate(viewDate.add(1, 'month'))}
        onSuperNext={() => setViewDate(viewDate.add(1, 'year'))}
        labels={labels}
      >
        {lang.monthBeforeYear === false
          ? [yearBtn, monthBtn]
          : [monthBtn, yearBtn]}
      </PanelHeader>
      <DateBody
        {...shared}
        disabledDate={(date) => isDisabledDate(date, 'date')}
        selected={picker === 'week' ? null : selected}
        weekSelected={picker === 'week' ? selected : null}
        rangeSelection={rangeSelection}
        onPick={onPick}
        showWeekNumber={showWeekNumber || picker === 'week'}
      />
    </div>
  )
}

/* ---------------------------------------------------------------- the shell */

type ShellProps = {
  rootRef: React.Ref<HTMLDivElement>
  disabled: boolean
  status?: string
  size?: SharedProps['size']
  variant?: SharedProps['variant']
  bordered?: SharedProps['bordered']
  open: boolean
  isRange: boolean
  className?: string
  rootClassName?: string
  style?: React.CSSProperties
  children: React.ReactNode
  domProps: Record<string, unknown>
  /** Every text input the shell wraps, in document order. */
  inputs: React.RefObject<HTMLInputElement>[]
  /** Index into `inputs` of the first input that is not disabled, or -1. */
  enabledInputIndex: number
  onRequestOpen: () => void
}

const PickerShell = ({
  rootRef,
  disabled,
  status,
  size,
  variant,
  bordered,
  open,
  isRange,
  className,
  rootClassName,
  style,
  children,
  domProps,
  inputs,
  enabledInputIndex,
  onRequestOpen,
}: ShellProps) => {
  /* antd (rc-picker) opens the panel from a handler on the `.ant-picker` ROOT,
     not from the input — so a click on the suffix icon, the field's padding or
     a range's separator opens it too (`SinglePicker`'s `onSelectorClick`,
     `RangePicker`'s equivalent, which focuses the first ENABLED input). The
     root also carries rc-picker's `onMouseDown` guard: preventDefault for any
     target that is not one of the inputs, or the click would pull focus off
     the input that was just focused (`Selector/RangeSelector`). */
  const isInputTarget = (target: EventTarget | null) =>
    inputs.some((input) => input.current && input.current === target)

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!isInputTarget(event.target)) event.preventDefault()
    ;(
      domProps.onMouseDown as
        | React.MouseEventHandler<HTMLDivElement>
        | undefined
    )?.(event)
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!disabled) {
      if (!event.currentTarget.contains(document.activeElement))
        inputs[enabledInputIndex]?.current?.focus()
      onRequestOpen()
    }
    ;(
      domProps.onClick as React.MouseEventHandler<HTMLDivElement> | undefined
    )?.(event)
  }

  return (
    <PopoverAnchor asChild>
      {/* The field's keyboard surface is the <input> it wraps, exactly as
          antd's own `.ant-picker` div is. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        ref={rootRef}
        data-slot="picker"
        data-range={isRange || undefined}
        data-open={open || undefined}
        data-disabled={disabled || undefined}
        data-status={status || undefined}
        data-size={size ?? 'middle'}
        className={cn(
          /* Always inline, never `w-full` — antd's own `.ant-picker` is
             inline-block and sizes to its input, so a picker inside a
             `Form.Item` must NOT stretch to the field width. The ~50 call sites
             that want a full-width date field already pass an explicit
             `style={{ width: '100%' }}`, which lands on this same element and
             beats any utility here; widening the rest would be a layout change
             with the toggle on that nobody asked for. Full-width WAS offered as
             the new-design treatment and declined (PRO-736.19 review). */
          'group/picker relative inline-flex items-center gap-2 rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow] dark:bg-input/30',
          SIZE_CLASS[size ?? 'middle'],
          variantClass(variant, bordered),
          disabled && 'cursor-not-allowed bg-muted opacity-60',
          status === 'error' && 'border-destructive',
          status === 'warning' && 'border-wh-gold-500',
          open && 'border-ring ring-[3px] ring-ring/50',
          rootClassName,
          className
        )}
        style={style}
        {...domProps}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        {children}
      </div>
    </PopoverAnchor>
  )
}

const inputClass =
  'w-full min-w-0 bg-transparent p-0 outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed'

const NON_DOM_PROPS = new Set([
  'theme',
  'forwardedAs',
  'prefixCls',
  'transitionName',
  'dropdownAlign',
  'popupAlign',
  'direction',
  'generateConfig',
  'prevIcon',
  'nextIcon',
  'superPrevIcon',
  'superNextIcon',
  'components',
  'order',
  'presets',
  'ranges',
  'separator',
  'onPanelChange',
  'onCalendarChange',
  'onSelect',
  'multiple',
  'needConfirm',
  'preserveInvalidOnBlur',
  'pickerValue',
  'onPickerValueChange',
  'defaultOpen',
  'mode',
  'hideHeader',
  'clearIcon',
  'allowEmpty',
  'styles',
  'classNames',
  'showWeek',
  'defaultOpenValue',
  'builtinPlacements',
  'changeOnBlur',
  'inputRender',
  'monthCellRender',
  'showHour',
  'showMinute',
  'showSecond',
  'showMillisecond',
  'use12Hours',
  'hourStep',
  'minuteStep',
  'secondStep',
  'millisecondStep',
  'hideDisabledOptions',
  'disabledHours',
  'disabledMinutes',
  'disabledSeconds',
  'changeOnScroll',
  'removeIcon',
  'maxTagCount',
])

/**
 * antd (rc-picker) spreads leftover props onto the INNER `<input>`, not the
 * picker root — which is why `data-testid` on a `<DatePicker>` resolves to the
 * input at every call site that uses one (EffectiveControl, OfficeProofLookup,
 * …), and why `getByTestId(...)` then `.closest('[data-slot="picker"]')` works.
 * Split the same way: `data-*` / `aria-*` go to the input, everything else the
 * DOM will accept stays on the root.
 */
const splitDomProps = (rest: Record<string, unknown>) => {
  const root: Record<string, unknown> = {}
  const input: Record<string, unknown> = {}
  Object.keys(rest).forEach((key) => {
    if (NON_DOM_PROPS.has(key)) return
    if (key.startsWith('data-') || key.startsWith('aria-'))
      input[key] = rest[key]
    else root[key] = rest[key]
  })
  return { root, input }
}

/**
 * The offset between the two panels of a range picker. The YEAR panel renders
 * a DECADE (see `decadeStart` in `./panel`), so its second panel is ten years
 * on — a one-year step would leave both panels showing the same ten years.
 */
const panelStep = (
  panelMode: PanelMode
): { count: number; unit: 'month' | 'year' } =>
  panelMode === 'date' || panelMode === 'week'
    ? { count: 1, unit: 'month' }
    : { count: panelMode === 'year' ? 10 : 1, unit: 'year' }

const addPanelStep = (date: Dayjs, panelMode: PanelMode) => {
  const { count, unit } = panelStep(panelMode)
  return date.add(count, unit)
}

const subtractPanelStep = (date: Dayjs, panelMode: PanelMode) => {
  const { count, unit } = panelStep(panelMode)
  return date.subtract(count, unit)
}

/**
 * `cellRender` (antd 5) supersedes `dateRender` (antd 4, still used by
 * `useDatePickerBaseProps`'s callers). Resolve one renderer from whichever the
 * call site passed.
 */
const resolveCellRender = (
  cellRender: SharedProps['cellRender'],
  dateRender: SharedProps['dateRender']
): CellRender | undefined => {
  if (cellRender) return cellRender as CellRender
  if (dateRender) return (current, info) => dateRender(current, info.today)
  return undefined
}

const resolvePortalContainer = (
  rootNode: HTMLDivElement | null,
  getPopupContainer: SharedProps['getPopupContainer']
) => {
  if (typeof getPopupContainer !== 'function' || !rootNode) return undefined
  return getPopupContainer(rootNode) ?? undefined
}

/**
 * The root node lives in STATE, not only in a ref — the same shape
 * `../Select/Select.shadcn.tsx` uses. A container resolved in an effect leaves
 * the first commit with `undefined`, so a picker that is OPEN FROM MOUNT
 * (`FilterMenuDatePicker` passes `open`; `RangeSelectorWithRadio` drives it)
 * portals its panel to the body and Radix then remounts the content when the
 * container prop changes. Holding the node in state re-renders before paint
 * with the real container, and re-reading it on every render also covers the
 * lazily-mounted case (a picker inside a Modal/Drawer has no `parentElement`
 * on its first pass).
 */
const useRootNode = () => {
  const rootRef = React.useRef<HTMLDivElement>(null)
  const [rootNode, setRootNode] = React.useState<HTMLDivElement | null>(null)
  const setRootRef = React.useCallback((node: HTMLDivElement | null) => {
    ;(rootRef as React.MutableRefObject<HTMLDivElement | null>).current = node
    setRootNode(node)
  }, [])
  return { rootRef, rootNode, setRootRef }
}

/* --------------------------------------------------------------- single */

const InternalDatePicker = React.forwardRef<PickerRefLike, $TSFixMe>(
  (props, ref) => {
    const {
      value,
      defaultValue,
      onChange,
      picker = 'date',
      format,
      showTime,
      showNow,
      showToday,
      placeholder,
      disabled: disabledProp,
      inputReadOnly,
      allowClear = true,
      autoFocus,
      open: openProp,
      onOpenChange,
      placement = 'bottomLeft',
      size,
      status: statusProp,
      variant,
      bordered,
      suffixIcon,
      locale: localeProp,
      minDate,
      maxDate,
      defaultPickerValue,
      disabledDate,
      disabledTime,
      cellRender,
      dateRender,
      panelRender,
      renderExtraFooter,
      getPopupContainer,
      onFocus,
      onBlur,
      onOk,
      showWeekNumber,
      className,
      rootClassName,
      style,
      popupClassName,
      dropdownClassName,
      popupStyle,
      id,
      name,
      ...rest
    } = props as SharedProps & $TSFixMe

    const contextDisabled = React.useContext(DisabledContext)
    const formItemContext = React.useContext(FormItemInputContext)
    const disabled = disabledProp ?? contextDisabled ?? false
    const status = statusProp || formItemContext?.status

    const locale = usePickerLocale(localeProp)
    const localeName = useDayjsLocaleName(locale)
    const masks = React.useMemo(
      () => resolveFormats(format, picker, showTime),
      [format, picker, showTime]
    )
    const timeConfig = toShowTimeConfig(showTime, picker)
    const isDisabledDate = useDisabledPredicate(disabledDate, minDate, maxDate)

    const { rootRef, rootNode, setRootRef } = useRootNode()
    const inputRef = React.useRef<HTMLInputElement>(null)
    const domProps = splitDomProps(rest as Record<string, unknown>)

    const [mergedValue, setMergedValue] = useMergedState<Dayjs | null>(null, {
      value,
      defaultValue,
    })
    const [open, setOpenState] = useMergedState<boolean>(false, {
      value: openProp,
    })
    const setOpen = (next: boolean) => {
      if (openProp === undefined) setOpenState(next)
      if (next !== open) onOpenChange?.(next)
    }

    /** The value the panel is editing; committed on click (or on OK). */
    const [draft, setDraft] = React.useState<Dayjs | null>(mergedValue)
    /**
     * The last value this picker itself emitted. Anything else arriving in
     * `mergedValue` came from OUTSIDE (a parent's quick-select, a form reset)
     * and has to be pulled into the panel even while it stays open.
     */
    const lastEmittedRef = React.useRef<Dayjs | null | undefined>(mergedValue)
    const [text, setText] = React.useState<string | null>(null)
    const [panelMode, setPanelMode] = React.useState<PanelMode>(() =>
      initialPanelMode(picker)
    )
    const [viewDate, setViewDate] = React.useState<Dayjs>(
      () => mergedValue ?? defaultPickerValue ?? dayjs()
    )

    React.useEffect(() => {
      if (open) {
        setDraft(mergedValue)
        setPanelMode(initialPanelMode(picker))
        setViewDate(mergedValue ?? defaultPickerValue ?? dayjs())
        setText(null)
      }
      // Re-syncing on every `mergedValue` tick would fight the user's own
      // in-panel edits, so this deliberately keys off `open` only.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // …but a value that did NOT come from this picker still has to land in the
    // panel, or an always-open picker (FilterMenuDatePicker) keeps showing the
    // range its parent has already moved on from.
    React.useEffect(() => {
      if (isSameCell(mergedValue, lastEmittedRef.current, picker, showTime))
        return
      lastEmittedRef.current = mergedValue
      setDraft(mergedValue)
      setViewDate(mergedValue ?? defaultPickerValue ?? dayjs())
      setText(null)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedValue])

    const displayText =
      text ??
      formatValue(open ? draft : mergedValue, masks as $TSFixMe, localeName)

    const emit = (next: Dayjs | null) => {
      lastEmittedRef.current = next
      if (isSameCell(next, mergedValue, picker, showTime)) return
      setMergedValue(next)
      onChange?.(next, formatValue(next, masks as $TSFixMe, localeName))
    }

    const commit = (next: Dayjs | null, close = true) => {
      emit(next)
      setText(null)
      if (close) setOpen(false)
    }

    const needsConfirm = Boolean(timeConfig)

    /**
     * antd v5's own defaults: a plain `picker="date"` shows the "Today" link,
     * and the same picker with `showTime` shows "Now" beside OK. Passing
     * `showToday={false}` / `showNow={false}` still suppresses it, and every
     * other picker mode keeps antd's "only when asked" behaviour.
     */
    const mergedShowNow = showNow ?? (picker === 'date' && needsConfirm)
    const mergedShowToday = showToday ?? (picker === 'date' && !needsConfirm)
    const showTodayOrNow = Boolean(mergedShowNow || mergedShowToday)

    const handlePick = (date: Dayjs) => {
      const snapped = snapToPicker(date, picker)
      if (needsConfirm) {
        // Keep whatever time is already on the draft (antd does the same) so
        // picking a different day never resets the hour the user just set.
        const base = draft ?? mergedValue ?? timeConfig?.defaultValue ?? snapped
        const merged = snapped
          .hour((base as Dayjs).hour())
          .minute((base as Dayjs).minute())
          .second((base as Dayjs).second())
        setDraft(merged)
        setViewDate(merged)
        return
      }
      commit(snapped)
    }

    const clear = (event: React.MouseEvent) => {
      event.stopPropagation()
      commit(null, false)
      setDraft(null)
    }

    React.useImperativeHandle(ref, () => ({
      nativeElement: rootRef.current,
      focus: (options?: FocusOptions) => inputRef.current?.focus(options),
      blur: () => inputRef.current?.blur(),
    }))

    const portalContainer = resolvePortalContainer(rootNode, getPopupContainer)

    const legacyCellRender = resolveCellRender(cellRender, dateRender)

    const commitTypedText = () => {
      if (text == null) return
      const parsed = parseValue(text, masks as $TSFixMe, localeName)
      if (parsed && !isDisabledDate(parsed, panelMode)) commit(parsed)
      else setText(null)
    }

    /* rc-picker's clear icon depends only on `allowClear`, a value being
       present and not-disabled — `inputReadOnly` does NOT suppress it, and the
       range half below agrees. */
    const showClear = Boolean(allowClear) && Boolean(mergedValue) && !disabled

    const clearIcon = (typeof allowClear === 'object' &&
      allowClear.clearIcon) || <CloseCircleFilled />

    const footer = (
      <>
        {showTodayOrNow && !disabled && (
          <button
            type="button"
            data-slot="picker-now"
            className="px-3 py-2 text-sm text-primary hover:underline"
            onClick={() => {
              const now = dayjs()
              if (needsConfirm) {
                setDraft(now)
                setViewDate(now)
              } else commit(snapToPicker(now, picker))
            }}
          >
            {mergedShowNow
              ? locale?.lang?.now ?? 'Now'
              : locale?.lang?.today ?? 'Today'}
          </button>
        )}
        {renderExtraFooter?.(panelMode)}
        {needsConfirm && (
          <button
            type="button"
            data-slot="picker-ok"
            className="ml-auto rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
            disabled={!draft}
            onClick={() => {
              if (!draft) return
              commit(draft)
              onOk?.(draft)
            }}
          >
            {locale?.lang?.ok ?? 'OK'}
          </button>
        )}
      </>
    )

    const hasFooter =
      showTodayOrNow || needsConfirm || Boolean(renderExtraFooter)

    let panel: React.ReactNode = (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        data-slot="picker-panels"
        className="flex"
        // Keeps focus in the input while the panel is clicked, so the control
        // never blurs (and commits typed text) mid-pick. Not an interactive
        // element itself: every actual control inside is a <button>.
        onMouseDown={(event) => event.preventDefault()}
      >
        {picker === 'time' ? (
          <div data-slot="picker-panel" data-mode="time">
            <TimeBody
              value={draft ?? mergedValue ?? dayjs().startOf('day')}
              config={{
                format: typeof masks[0] === 'string' ? masks[0] : undefined,
                ...timeConfig,
              }}
              disabledTime={disabledTime}
              onChange={setDraft}
            />
          </div>
        ) : (
          <PickerPanel
            picker={picker}
            panelMode={panelMode}
            setPanelMode={setPanelMode}
            viewDate={viewDate}
            setViewDate={setViewDate}
            localeName={localeName}
            locale={locale}
            isDisabledDate={isDisabledDate}
            cellRender={legacyCellRender}
            selected={needsConfirm ? draft : mergedValue}
            onPick={handlePick}
            showWeekNumber={showWeekNumber}
          />
        )}
        {timeConfig && picker !== 'time' && panelMode === 'date' && (
          <TimeBody
            value={draft ?? dayjs().startOf('day')}
            config={{ format: timeFormatFor(showTime ?? true), ...timeConfig }}
            disabledTime={disabledTime}
            onChange={setDraft}
          />
        )}
      </div>
    )

    panel = (
      <>
        {panel}
        {hasFooter && (
          <div
            data-slot="picker-footer"
            className="flex items-center gap-2 border-t px-2 py-1"
          >
            {footer}
          </div>
        )}
      </>
    )

    if (panelRender) panel = panelRender(panel)

    const side = placement.startsWith('top') ? 'top' : 'bottom'
    const align = placement.endsWith('Right') ? 'end' : 'start'

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PickerShell
          rootRef={setRootRef}
          disabled={disabled}
          status={status}
          size={size}
          variant={variant}
          bordered={bordered}
          open={open}
          isRange={false}
          className={className}
          rootClassName={rootClassName}
          style={style}
          domProps={domProps.root}
          inputs={[inputRef]}
          enabledInputIndex={0}
          onRequestOpen={() => setOpen(true)}
        >
          <span
            data-slot="picker-input"
            className="relative flex min-w-0 flex-1 items-center"
          >
            <input
              {...(domProps.input as React.InputHTMLAttributes<HTMLInputElement>)}
              ref={inputRef}
              id={id}
              name={name}
              type="text"
              autoComplete="off"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={autoFocus}
              readOnly={Boolean(inputReadOnly)}
              disabled={disabled}
              placeholder={
                (placeholder as string) ??
                (placeholderFor(locale, picker, false) as string)
              }
              value={displayText}
              className={inputClass}
              onMouseDown={() => {
                if (!disabled) setOpen(true)
              }}
              onFocus={(event) => {
                if (!disabled) setOpen(true)
                onFocus?.(event)
              }}
              onBlur={(event) => {
                commitTypedText()
                onBlur?.(event)
              }}
              onChange={(event) => {
                setText(event.target.value)
                const parsed = parseValue(
                  event.target.value,
                  masks as $TSFixMe,
                  localeName
                )
                if (parsed) setViewDate(parsed)
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commitTypedText()
                  setOpen(false)
                } else if (event.key === 'Escape') {
                  setText(null)
                  setOpen(false)
                }
              }}
            />
          </span>
          <span
            data-slot="picker-suffix"
            aria-hidden
            className={cn(
              'pointer-events-none flex items-center text-xs text-muted-foreground',
              showClear && 'group-hover/picker:invisible'
            )}
          >
            {suffixIcon ?? defaultSuffixIcon(picker)}
          </span>
          {showClear && (
            <button
              type="button"
              data-slot="picker-clear"
              aria-label={locale?.lang?.clear ?? 'clear'}
              className="absolute right-3 hidden items-center text-xs text-muted-foreground group-hover/picker:flex hover:text-foreground"
              onMouseDown={(event) => event.preventDefault()}
              onClick={clear}
            >
              {clearIcon}
            </button>
          )}
        </PickerShell>

        <PopoverContent
          data-slot="picker-dropdown"
          side={side}
          align={align}
          sideOffset={4}
          className={cn(
            'w-auto overflow-hidden p-0',
            popupClassName,
            dropdownClassName
          )}
          style={popupStyle}
          container={portalContainer}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (rootRef.current?.contains(event.target as Node))
              event.preventDefault()
          }}
          onFocusOutside={(event) => {
            if (rootRef.current?.contains(event.target as Node))
              event.preventDefault()
          }}
        >
          {panel}
        </PopoverContent>
      </Popover>
    )
  }
)

InternalDatePicker.displayName = 'DatePicker'

/* ---------------------------------------------------------------- range */

type RangeValue = [Dayjs | null, Dayjs | null] | null

const InternalRangePicker = React.forwardRef<PickerRefLike, $TSFixMe>(
  (props, ref) => {
    const {
      value,
      defaultValue,
      onChange,
      onCalendarChange,
      picker = 'date',
      format,
      showTime,
      // antd's range panel has no Today/Now shortcut (only the single picker
      // does), but both props are still destructured so neither can leak onto
      // the root <div> through the DOM-prop split.
      showNow: _showNow,
      showToday: _showToday,
      placeholder,
      disabled: disabledProp,
      inputReadOnly,
      allowClear = true,
      allowEmpty,
      autoFocus,
      open: openProp,
      onOpenChange,
      placement = 'bottomLeft',
      size,
      status: statusProp,
      variant,
      bordered,
      suffixIcon,
      separator,
      locale: localeProp,
      minDate,
      maxDate,
      defaultPickerValue,
      disabledDate,
      disabledTime,
      cellRender,
      dateRender,
      panelRender,
      renderExtraFooter,
      getPopupContainer,
      onFocus,
      onBlur,
      onOk,
      showWeekNumber,
      className,
      rootClassName,
      style,
      popupClassName,
      dropdownClassName,
      popupStyle,
      id,
      name,
      ...rest
    } = props as SharedProps & $TSFixMe

    const contextDisabled = React.useContext(DisabledContext)
    const formItemContext = React.useContext(FormItemInputContext)
    const disabledPair: [boolean, boolean] = Array.isArray(disabledProp)
      ? [Boolean(disabledProp[0]), Boolean(disabledProp[1])]
      : [
          Boolean(disabledProp ?? contextDisabled),
          Boolean(disabledProp ?? contextDisabled),
        ]
    const disabled = disabledPair[0] && disabledPair[1]
    const status = statusProp || formItemContext?.status

    const locale = usePickerLocale(localeProp)
    const localeName = useDayjsLocaleName(locale)
    const masks = React.useMemo(
      () => resolveFormats(format, picker, showTime),
      [format, picker, showTime]
    )
    const isDisabledDate = useDisabledPredicate(disabledDate, minDate, maxDate)
    const timeConfig = toShowTimeConfig(showTime, picker)
    const needsConfirm = Boolean(timeConfig)
    /**
     * `showTime.defaultValue` is a PAIR for a range (antd types it
     * `Dayjs | Dayjs[]`); either end falls back to midnight.
     */
    const timeDefaults: [Dayjs, Dayjs] = React.useMemo(() => {
      const midnight = dayjs().startOf('day')
      const configured = timeConfig?.defaultValue
      const pair = Array.isArray(configured)
        ? configured
        : [configured, configured]
      return [pair[0] ?? midnight, pair[1] ?? midnight]
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [timeConfig?.defaultValue])

    const { rootRef, rootNode, setRootRef } = useRootNode()
    const domProps = splitDomProps(rest as Record<string, unknown>)
    const inputRefs = [
      React.useRef<HTMLInputElement>(null),
      React.useRef<HTMLInputElement>(null),
    ]

    const [mergedValue, setMergedValue] = useMergedState<RangeValue>(null, {
      value,
      defaultValue,
    })
    const [open, setOpenState] = useMergedState<boolean>(false, {
      value: openProp,
    })
    const setOpen = (next: boolean) => {
      if (openProp === undefined) setOpenState(next)
      if (next !== open) onOpenChange?.(next)
    }

    const [activeIndex, setActiveIndex] = React.useState<0 | 1>(0)
    const [draft, setDraft] = React.useState<[Dayjs | null, Dayjs | null]>([
      mergedValue?.[0] ?? null,
      mergedValue?.[1] ?? null,
    ])
    const [texts, setTexts] = React.useState<[string | null, string | null]>([
      null,
      null,
    ])
    /** See the single picker: what THIS picker last emitted. */
    const lastEmittedRef = React.useRef<RangeValue | undefined>(mergedValue)
    const [panelMode, setPanelMode] = React.useState<PanelMode>(() =>
      initialPanelMode(picker)
    )
    const [viewDate, setViewDate] = React.useState<Dayjs>(
      () => mergedValue?.[0] ?? defaultPickerValue ?? dayjs()
    )

    React.useEffect(() => {
      if (open) {
        setDraft([mergedValue?.[0] ?? null, mergedValue?.[1] ?? null])
        setPanelMode(initialPanelMode(picker))
        setViewDate(
          (activeIndex === 1 ? mergedValue?.[1] : mergedValue?.[0]) ??
            defaultPickerValue ??
            dayjs()
        )
        setTexts([null, null])
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    // An externally-set range (FilterMenuDatePicker's quick-select radios drive
    // a picker that is permanently `open`) must reach the panel too.
    React.useEffect(() => {
      const emitted = lastEmittedRef.current
      if (
        isSameCell(mergedValue?.[0], emitted?.[0], picker, showTime) &&
        isSameCell(mergedValue?.[1], emitted?.[1], picker, showTime)
      )
        return
      lastEmittedRef.current = mergedValue
      setDraft([mergedValue?.[0] ?? null, mergedValue?.[1] ?? null])
      // A value from outside means the user is not mid-range, so editing goes
      // back to the start end.
      setActiveIndex(0)
      setViewDate(mergedValue?.[0] ?? defaultPickerValue ?? dayjs())
      setTexts([null, null])
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mergedValue])

    const formatOne = (date: Dayjs | null) =>
      formatValue(date, masks as $TSFixMe, localeName)

    const shown = open ? draft : [mergedValue?.[0], mergedValue?.[1]]
    const displayTexts: [string, string] = [
      texts[0] ?? formatOne(shown[0] ?? null),
      texts[1] ?? formatOne(shown[1] ?? null),
    ]

    const emit = (next: RangeValue) => {
      lastEmittedRef.current = next
      const same =
        isSameCell(next?.[0], mergedValue?.[0], picker, showTime) &&
        isSameCell(next?.[1], mergedValue?.[1], picker, showTime)
      if (same) return
      setMergedValue(next)
      const strings: [string, string] = [
        formatOne(next?.[0] ?? null),
        formatOne(next?.[1] ?? null),
      ]
      onChange?.(next, strings)
    }

    /**
     * antd only fires `onChange` once the range is COMPLETE (unless
     * `allowEmpty` opens that side), while `onCalendarChange` fires on every
     * partial pick. Call sites rely on that: several read `v[0]`/`v[1]`
     * unguarded inside `onChange`.
     */
    const isComplete = (next: [Dayjs | null, Dayjs | null]) =>
      next[0] != null && next[1] != null

    /**
     * `allowEmpty` says a SUBMITTED range may leave that end out; it never
     * means an end can be skipped mid-interaction. So it relaxes only the
     * explicit confirm path (the OK button), never the automatic emit above —
     * otherwise the first day click writes a half range into the parent's
     * state, which is what the two `allowEmpty={[true, true]}` call sites
     * (the warehouse import modal's warranty range and NyOrdreDrawer's
     * requested window) would receive as a finished value.
     */
    const isSubmittable = (next: [Dayjs | null, Dayjs | null]) => {
      const emptyOk = Array.isArray(allowEmpty)
        ? allowEmpty
        : [Boolean(allowEmpty), Boolean(allowEmpty)]
      return (next[0] != null || emptyOk[0]) && (next[1] != null || emptyOk[1])
    }

    const applyDraft = (next: [Dayjs | null, Dayjs | null], close: boolean) => {
      setDraft(next)
      onCalendarChange?.(next, [formatOne(next[0]), formatOne(next[1])], {
        range: activeIndex === 0 ? 'start' : 'end',
      })
      // Under `showTime` the value is the OK button's to emit (antd's rule),
      // so a complete draft is still only a draft here.
      if (!needsConfirm && isComplete(next)) {
        emit(next)
        // antd cycles back to the start field once a range is whole; an
        // always-open picker (FilterMenuDatePicker collapses its own inputs to
        // opacity:0) has no input to click to get back there otherwise.
        setActiveIndex(0)
        if (close) {
          setTexts([null, null])
          setOpen(false)
        }
      }
    }

    /**
     * Keep the time already on that end of the range (antd does the same), so
     * moving a day never resets the hour the user set in the time columns.
     */
    const withDraftTime = (date: Dayjs, index: 0 | 1) => {
      if (!needsConfirm) return date
      const base = draft[index] ?? timeDefaults[index]
      return date.hour(base.hour()).minute(base.minute()).second(base.second())
    }

    /**
     * Closing is antd's other SUBMIT point: a draft the OK button would accept
     * is emitted here, which is the only way an `allowEmpty` end ever reaches
     * `onChange` now that a partial pick no longer does.
     */
    const handleOpenChange = (next: boolean) => {
      if (!next) {
        if (!needsConfirm && isSubmittable(draft)) emit(draft)
        setTexts([null, null])
        setActiveIndex(0)
      }
      setOpen(next)
    }

    const handlePick = (rawDate: Dayjs) => {
      const date = withDraftTime(rawDate, activeIndex)
      const snapped = snapToPicker(date, picker)
      if (activeIndex === 0) {
        // Picking a start begins a fresh range whenever the draft is already
        // whole (antd asks for the end again rather than silently re-emitting
        // with the old one), and drops an end the new start would invert.
        const restarts = draft[0] != null && draft[1] != null
        const nextEnd =
          restarts || (draft[1] && snapped.isAfter(draft[1], 'day'))
            ? null
            : draft[1]
        const next: [Dayjs | null, Dayjs | null] = [snapped, nextEnd]
        setActiveIndex(1)
        applyDraft(next, !needsConfirm && nextEnd != null)
        if (!needsConfirm && nextEnd == null) inputRefs[1].current?.focus()
        return
      }
      const nextStart =
        draft[0] && snapped.isBefore(draft[0], 'day') ? null : draft[0]
      const next: [Dayjs | null, Dayjs | null] = [nextStart, snapped]
      applyDraft(next, !needsConfirm)
    }

    React.useImperativeHandle(ref, () => ({
      nativeElement: rootRef.current,
      focus: (options?: FocusOptions) => inputRefs[0].current?.focus(options),
      blur: () => {
        inputRefs[0].current?.blur()
        inputRefs[1].current?.blur()
      },
    }))

    const portalContainer = resolvePortalContainer(rootNode, getPopupContainer)

    const legacyCellRender = resolveCellRender(cellRender, dateRender)

    const commitTypedText = (index: 0 | 1) => {
      const raw = texts[index]
      if (raw == null) return
      const parsed = parseValue(raw, masks as $TSFixMe, localeName)
      const next: [Dayjs | null, Dayjs | null] = [draft[0], draft[1]]
      if (parsed && !isDisabledDate(parsed, panelMode)) {
        next[index] = snapToPicker(parsed, picker)
        applyDraft(next, false)
      }
      setTexts((current) => {
        const copy: [string | null, string | null] = [current[0], current[1]]
        copy[index] = null
        return copy
      })
    }

    const hasValue = Boolean(mergedValue?.[0] || mergedValue?.[1])
    const showClear = Boolean(allowClear) && hasValue && !disabled

    const clearIcon = (typeof allowClear === 'object' &&
      allowClear.clearIcon) || <CloseCircleFilled />

    const placeholders = (placeholder ??
      placeholderFor(locale, picker, true)) as [string, string]

    const panels = (
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions
      <div
        data-slot="picker-panels"
        className="flex divide-x"
        // See the single picker: keeps focus in the active input while the
        // panel is clicked. Every control inside is a <button>.
        onMouseDown={(event) => event.preventDefault()}
      >
        <PickerPanel
          picker={picker}
          panelMode={panelMode}
          setPanelMode={setPanelMode}
          viewDate={viewDate}
          setViewDate={setViewDate}
          localeName={localeName}
          locale={locale}
          isDisabledDate={isDisabledDate}
          cellRender={legacyCellRender}
          rangeSelection={{ from: draft[0], to: draft[1] }}
          onPick={handlePick}
          showWeekNumber={showWeekNumber}
          showNext={false}
          rangeSide="start"
        />
        <PickerPanel
          picker={picker}
          panelMode={panelMode}
          setPanelMode={setPanelMode}
          viewDate={addPanelStep(viewDate, panelMode)}
          setViewDate={(next) =>
            setViewDate(subtractPanelStep(next, panelMode))
          }
          localeName={localeName}
          locale={locale}
          isDisabledDate={isDisabledDate}
          cellRender={legacyCellRender}
          rangeSelection={{ from: draft[0], to: draft[1] }}
          onPick={handlePick}
          showWeekNumber={showWeekNumber}
          showPrev={false}
          rangeSide="end"
        />
        {timeConfig && panelMode === 'date' && (
          // antd edits the time of ONE end at a time — whichever input is
          // active — and writes it straight back into that end of the draft.
          <TimeBody
            value={draft[activeIndex] ?? timeDefaults[activeIndex]}
            config={{ format: timeFormatFor(showTime ?? true), ...timeConfig }}
            disabledTime={
              disabledTime &&
              ((current: Dayjs) =>
                disabledTime(current, activeIndex === 0 ? 'start' : 'end'))
            }
            onChange={(next) => {
              // The time columns EDIT an end, they never create one: without a
              // day the user actually clicked there is nothing to put an hour
              // on, and materialising today here would complete the draft with
              // a date nobody picked.
              if (!draft[activeIndex]) return
              const updated: [Dayjs | null, Dayjs | null] = [draft[0], draft[1]]
              updated[activeIndex] = next
              applyDraft(updated, false)
            }}
          />
        )}
      </div>
    )

    const hasFooter = needsConfirm || Boolean(renderExtraFooter)

    let panel: React.ReactNode = (
      <>
        {panels}
        {hasFooter && (
          <div
            data-slot="picker-footer"
            className="flex items-center gap-2 border-t px-2 py-1"
          >
            {renderExtraFooter?.(panelMode)}
            {needsConfirm && (
              <button
                type="button"
                data-slot="picker-ok"
                className="ml-auto rounded-md bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
                disabled={!isSubmittable(draft)}
                onClick={() => {
                  emit(draft)
                  setTexts([null, null])
                  setActiveIndex(0)
                  setOpen(false)
                  onOk?.(draft)
                }}
              >
                {locale?.lang?.ok ?? 'OK'}
              </button>
            )}
          </div>
        )}
      </>
    )

    if (panelRender) panel = panelRender(panel)

    const side = placement.startsWith('top') ? 'top' : 'bottom'
    const align = placement.endsWith('Right') ? 'end' : 'start'

    const renderInput = (index: 0 | 1) => (
      <span
        data-slot="picker-input"
        data-active={open && activeIndex === index ? true : undefined}
        className="relative flex min-w-0 flex-1 items-center"
      >
        <input
          {...(index === 0
            ? (domProps.input as React.InputHTMLAttributes<HTMLInputElement>)
            : {})}
          ref={inputRefs[index]}
          id={index === 0 ? id : undefined}
          name={index === 0 ? name : undefined}
          type="text"
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={index === 0 ? autoFocus : undefined}
          readOnly={Boolean(inputReadOnly)}
          disabled={disabledPair[index]}
          placeholder={placeholders?.[index]}
          value={displayTexts[index]}
          className={inputClass}
          onMouseDown={() => {
            if (disabledPair[index]) return
            setActiveIndex(index)
            setOpen(true)
          }}
          onFocus={(event) => {
            if (disabledPair[index]) return
            setActiveIndex(index)
            setOpen(true)
            onFocus?.(event)
          }}
          onBlur={(event) => {
            commitTypedText(index)
            onBlur?.(event)
          }}
          onChange={(event) => {
            const next: [string | null, string | null] = [texts[0], texts[1]]
            next[index] = event.target.value
            setTexts(next)
            const parsed = parseValue(
              event.target.value,
              masks as $TSFixMe,
              localeName
            )
            if (parsed) setViewDate(parsed)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitTypedText(index)
            } else if (event.key === 'Escape') {
              setTexts([null, null])
              setOpen(false)
            }
          }}
        />
      </span>
    )

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PickerShell
          rootRef={setRootRef}
          disabled={disabled}
          status={status}
          size={size}
          variant={variant}
          bordered={bordered}
          open={open}
          isRange
          className={className}
          rootClassName={rootClassName}
          style={style}
          domProps={domProps.root}
          inputs={inputRefs}
          enabledInputIndex={disabledPair.findIndex((each) => !each)}
          onRequestOpen={() => setOpen(true)}
        >
          {renderInput(0)}
          <span
            data-slot="picker-range-separator"
            aria-hidden
            className="shrink-0 text-muted-foreground"
          >
            {separator ?? '→'}
          </span>
          {renderInput(1)}
          <span
            data-slot="picker-suffix"
            aria-hidden
            className={cn(
              'pointer-events-none flex items-center text-xs text-muted-foreground',
              showClear && 'group-hover/picker:invisible'
            )}
          >
            {suffixIcon ?? <CalendarOutlined />}
          </span>
          {showClear && (
            <button
              type="button"
              data-slot="picker-clear"
              aria-label={locale?.lang?.clear ?? 'clear'}
              className="absolute right-3 hidden items-center text-xs text-muted-foreground group-hover/picker:flex hover:text-foreground"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => {
                event.stopPropagation()
                setDraft([null, null])
                setTexts([null, null])
                emit(null)
                // antd hands the start field back after a clear; without this
                // the panel would still be "editing the end" and the next day
                // click could never complete a range.
                setActiveIndex(0)
                // Only while the panel is already open: focusing an input
                // opens it, and antd leaves a collapsed picker collapsed.
                if (open) inputRefs[0].current?.focus()
              }}
            >
              {clearIcon}
            </button>
          )}
        </PickerShell>

        <PopoverContent
          data-slot="picker-dropdown"
          data-range
          side={side}
          align={align}
          sideOffset={4}
          className={cn(
            'w-auto overflow-hidden p-0',
            popupClassName,
            dropdownClassName
          )}
          style={popupStyle}
          container={portalContainer}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            if (rootRef.current?.contains(event.target as Node))
              event.preventDefault()
          }}
          onFocusOutside={(event) => {
            if (rootRef.current?.contains(event.target as Node))
              event.preventDefault()
          }}
        >
          {panel}
        </PopoverContent>
      </Popover>
    )
  }
)

InternalRangePicker.displayName = 'RangePicker'

/* --------------------------------------------------------------- exports */

/**
 * Exported for `./index.tsx` alone. The presets (`MonthPicker`, `WeekPicker`,
 * …) are built THERE, off the dispatcher rather than off this half, so
 * `<DatePicker.MonthPicker>` follows the toggle like `<DatePicker picker=
 * "month">` does.
 */
export { InternalDatePicker as DatePickerShadcn }
export { InternalRangePicker as RangePickerShadcn }
